import { Col, Form } from "antd";
import { useForm } from "antd/es/form/Form";
import { Md5 } from "md5-typescript";
import { useCallback, useMemo } from "react";
import { useDispatch } from "react-redux";

import { FormComponent, type FormError } from "../../Form/types/types";
import { showWarningNotification } from "../../NotificationAlerts";
import { createEditableListFormField } from "../CreateEditableListFormField";
import { calculateFreeSpace, createEditColumn } from "../Helpers";
import { CancelButton, ListRow, SaveButton } from "../styles/Styles";
import { INDEX_PROP } from "../types/Constants";
import type { EditableItem } from "../types/EditableItem";
import type { EditableItemEditProps } from "../types/EditableItemEditProps";

export const EditableListItemEdit = <T extends EditableItem>(props: EditableItemEditProps<T>) => {
    const { value, onChange, columns } = props;
    const dispatch = useDispatch();
    const [form] = useForm();

    const onSaveCallback = () => {
        if (!onChange || !value || Array.isArray(value)) {
            return;
        }

        const updateData = () => {
            const formData = form.getFieldsValue();
            const updatedItem = {
                ...formData,
                index: value[INDEX_PROP],
                isEditing: false,
                isNew: false,
            };
            onChange(updatedItem);
        };
        const showNotification = (err: FormError) => {
            dispatch(
                showWarningNotification({
                    text: `There are ${err.errorFields.length} validation errors:
                    ${err.errorFields.map((field) => `\n ${field.name.join(".")}: ${field.errors.join("; ")}`).join("")}`,
                    autoCloseDisabled: true,
                }),
            );
        };

        form.validateFields()
            .then(updateData)
            .catch((err) => showNotification(err));
    };
    const saveAction = (
        <SaveButton key="save-action" name="editableList_save_button" onClick={onSaveCallback}>
            Save
        </SaveButton>
    );

    const onCancelCallback = () => {
        if (!value || !onChange) {
            return;
        }

        if (!Array.isArray(value) && value.isNew) {
            onChange(null, value[INDEX_PROP]);
        } else {
            const updatedItem = {
                ...value,
                isEditing: false,
            };
            onChange(updatedItem);
        }
    };
    const cancelAction = (
        <CancelButton key="cancel-action" name="editableList_cancel_button" onClick={onCancelCallback}>
            Cancel
        </CancelButton>
    );

    const freeSpace = useCallback(() => {
        return calculateFreeSpace(columns || []);
    }, [columns]);

    const idField = createEditableListFormField({
        field: {
            name: "id",
            component: FormComponent.HIDDEN_INPUT,
            translationKeys: { label: "" },
        },
        rules: [],
        readOnly: false,
    });

    const key = useMemo(() => Md5.init(JSON.stringify(value)), [value]);
    return (
        (value && (
            <Form form={form} initialValues={value} key={`${key}-EditListForm`} component={false}>
                {idField}
                <ListRow key={`${key}-EditListRowRow`}>
                    {columns?.map((col) => createEditColumn(col))}
                    <Col span={freeSpace()} key={`${idField}EditActions`}>
                        {[saveAction, cancelAction]}
                    </Col>
                </ListRow>
            </Form>
        )) ||
        null
    );
};
